import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Gamepad2, Trophy, Copy, Check, Brain, Type, Hash } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import HangmanGame from '../components/games/HangmanGame';
import TriviaGame from '../components/games/TriviaGame';
import ScrambleGame from '../components/games/ScrambleGame';
import { guestGet } from '../lib/guestStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAME_META = {
  hangman:  {
    label: 'Hangman', tag: 'Guess or hang',
    desc: '5 levels · 3 hints per word',
    Icon: Hash, accent: '#FF007F',
    img: 'https://images.pexels.com/photos/278918/pexels-photo-278918.jpeg?auto=compress&w=900',
  },
  trivia: {
    label: 'Indian Trivia', tag: 'Brain on fire',
    desc: 'Politics · Sports · Cinema',
    Icon: Brain, accent: '#39FF14',
    img: 'https://images.pexels.com/photos/1181263/pexels-photo-1181263.jpeg?auto=compress&w=900',
  },
  scramble: {
    label: 'Word Scramble', tag: 'Untangle it',
    desc: '5 scrambled Indian words',
    Icon: Type, accent: '#FFD700',
    img: 'https://images.pexels.com/photos/256472/pexels-photo-256472.jpeg?auto=compress&w=900',
  },
};

const GuestGamesPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [config, setConfig] = useState(null);
  const [activeGame, setActiveGame] = useState(null);
  const [content, setContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [copied, setCopied] = useState(false);

  const [guestName] = useState(() => guestGet('guestName') || '');
  const [guestPhone] = useState(() => guestGet('guestPhone') || '');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/games/sessions/${code}/config`);
        setConfig(r.data);
      } catch (err) {
        console.error('[GuestGames] session config failed:', err);
        setExpired(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const startGame = async (game) => {
    setLoadingContent(true);
    setActiveGame(game);
    setContent(null);
    try {
      const r = await axios.get(`${API}/games/sessions/${code}/content`, { params: { game } });
      setContent(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load game');
      setActiveGame(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const claimWin = async () => {
    if (!activeGame) return;
    try {
      const r = await axios.post(`${API}/games/sessions/${code}/win`, {
        game: activeGame,
        guest_name: guestName,
        guest_phone: guestPhone,
      });
      setCoupon(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate coupon');
    }
  };

  const exitGame = () => {
    setActiveGame(null);
    setContent(null);
  };

  const copyCoupon = async () => {
    if (!coupon?.coupon_code) return;
    try {
      await navigator.clipboard.writeText(coupon.coupon_code);
      setCopied(true);
      toast.success('Coupon code copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('[GuestGames] clipboard copy failed:', err);
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const goHome = () => {
    setCoupon(null);
    setActiveGame(null);
    setContent(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center" data-testid="games-loading">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  if (expired || !config) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center p-6">
        <Toaster position="top-center" />
        <div className="text-center max-w-sm" data-testid="games-expired">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gamepad2 className="w-8 h-8 text-gray-500" />
          </div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-2">Games unavailable</h1>
          <p className="text-gray-400">This venue hasn&apos;t enabled bar games, or the session has expired.</p>
          <Button variant="ghost" onClick={() => navigate(`/request/${code}`)} className="mt-6 border border-white/10 rounded-full text-xs text-gray-300 tracking-wider">
            Back to session
          </Button>
        </div>
      </div>
    );
  }

  // ---- Coupon success screen (cinematic) ----
  if (coupon) {
    const meta = GAME_META[coupon.game] || {};
    return (
      <div className="min-h-screen relative overflow-hidden bg-black" data-testid="games-coupon-page">
        {/* Backdrop */}
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/3945667/pexels-photo-3945667.jpeg?auto=compress&w=1200)' }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black" />
        <motion.div
          aria-hidden
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] rounded-full bg-[#FFD700]/25 blur-[140px]"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 7, repeat: Infinity }}
        />
        <motion.div
          aria-hidden
          className="absolute bottom-0 -left-32 w-[26rem] h-[26rem] rounded-full bg-[#FF007F]/20 blur-[140px]"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 9, repeat: Infinity, delay: 1.4 }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
        />

        <Toaster position="top-center" />

        <div className="relative w-full max-w-md mx-auto px-6 pt-8 pb-10 flex flex-col min-h-screen">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={goHome} className="text-white/50 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#FFD700] font-['JetBrains_Mono']">
              You won
            </span>
            <div className="w-5" />
          </div>

          {/* Trophy */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center text-center mt-4"
          >
            <motion.div
              animate={{
                boxShadow: ['0 0 40px rgba(255,215,0,0.35)', '0 0 90px rgba(255,215,0,0.7)', '0 0 40px rgba(255,215,0,0.35)'],
              }}
              transition={{ duration: 2.6, repeat: Infinity }}
              className="w-24 h-24 rounded-3xl bg-gradient-to-br from-[#FFD700]/25 to-[#FF007F]/15 ring-1 ring-[#FFD700]/60 flex items-center justify-center mb-6"
            >
              <Trophy className="w-12 h-12 text-[#FFD700]" strokeWidth={1.5} />
            </motion.div>

            <p className="text-[11px] uppercase tracking-[0.45em] text-white/50 font-['JetBrains_Mono'] mb-3">
              {meta.label || coupon.game} cleared
            </p>
            <h1 className="text-[58px] sm:text-7xl leading-[0.85] font-black text-white font-['Outfit'] tracking-[-0.045em]">
              You<br />
              <span className="italic font-extralight text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#FF8C00] to-[#FF007F]">
                won it.
              </span>
            </h1>
          </motion.div>

          {/* Coupon ticket */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative mt-8"
          >
            <div className="absolute -top-2 left-6 right-6 h-1 bg-gradient-to-r from-[#FF007F] via-[#FFD700] to-[#39FF14] rounded-full opacity-80 blur-[2px]" />
            <div className="bg-white/[0.04] backdrop-blur-2xl border border-[#FFD700]/40 rounded-[28px] p-6 shadow-[0_30px_80px_-20px_rgba(255,215,0,0.35)]">
              <p className="text-[10px] uppercase tracking-[0.45em] text-[#FFD700] font-['JetBrains_Mono'] mb-2 text-center font-bold">
                Your reward
              </p>
              <p className="text-white text-xl font-extrabold text-center font-['Outfit']" data-testid="coupon-reward-text">
                {coupon.reward}
              </p>
              <div className="my-5 flex items-center gap-3">
                <div className="flex-1 border-t border-dashed border-white/15" />
                <span className="text-[9px] uppercase tracking-[0.35em] text-white/30 font-['JetBrains_Mono']">code</span>
                <div className="flex-1 border-t border-dashed border-white/15" />
              </div>
              <p
                className="text-3xl sm:text-4xl font-black text-white tracking-[0.32em] text-center font-['JetBrains_Mono']"
                data-testid="coupon-code"
              >
                {coupon.coupon_code}
              </p>
              <p className="text-white/40 text-[11px] text-center mt-3 font-light">
                Show this to the bar to redeem.
              </p>
            </div>
          </motion.div>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-6 space-y-3"
          >
            <motion.div whileTap={{ scale: 0.97 }} whileHover={{ scale: 1.01 }}>
              <Button
                onClick={copyCoupon}
                className="w-full h-14 bg-gradient-to-r from-[#FFD700] to-[#FF8C00] hover:opacity-95 text-black font-black rounded-2xl text-sm tracking-[0.35em] uppercase font-['Outfit'] shadow-[0_10px_40px_rgba(255,215,0,0.4)]"
                data-testid="coupon-copy"
              >
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy Code</>}
              </Button>
            </motion.div>
            <Button
              onClick={goHome}
              variant="ghost"
              className="w-full h-12 border border-white/15 text-white/80 hover:text-white rounded-2xl text-[11px] font-bold tracking-[0.4em] uppercase font-['JetBrains_Mono']"
              data-testid="coupon-play-more"
            >
              Play Another
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ---- Active game ----
  if (activeGame) {
    const meta = GAME_META[activeGame] || {};
    return (
      <div className="min-h-screen relative overflow-hidden bg-black flex flex-col" data-testid={`games-active-${activeGame}`}>
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0006] to-black" />
        <motion.div
          aria-hidden
          className="absolute -top-32 -left-32 w-[26rem] h-[26rem] rounded-full blur-[130px]"
          style={{ backgroundColor: `${meta.accent || '#FF007F'}30` }}
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <Toaster position="top-center" />
        <header className="relative px-5 py-5 border-b border-white/5 z-10">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <button onClick={exitGame} className="text-white/50 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <p className="text-white font-bold text-sm font-['Outfit']">{meta.label}</p>
              <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-['JetBrains_Mono'] mt-0.5">{config.venue_name}</p>
            </div>
            <div className="w-5" />
          </div>
        </header>
        <div className="relative flex-1 px-4 py-6 z-10">
          <div className="max-w-lg mx-auto">
            {loadingContent || !content ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: meta.accent }} />
              </div>
            ) : activeGame === 'hangman' ? (
              <HangmanGame rounds={content.rounds} onWin={claimWin} onExit={exitGame} />
            ) : activeGame === 'trivia' ? (
              <TriviaGame categories={content.categories} passThreshold={content.pass_threshold || 4} onWin={claimWin} onExit={exitGame} />
            ) : activeGame === 'scramble' ? (
              <ScrambleGame rounds={content.rounds} onWin={claimWin} onExit={exitGame} />
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // ---- Picker (cinematic bento) ----
  const games = (config.games || []).filter((g) => GAME_META[g]);
  return (
    <div className="min-h-screen relative overflow-hidden bg-black" data-testid="games-picker">
      {/* Atmospheric backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center opacity-35"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/3945667/pexels-photo-3945667.jpeg?auto=compress&w=1200)' }}
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/85 to-black" />
      <motion.div
        aria-hidden
        className="absolute -top-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#FF007F]/22 blur-[140px]"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-0 -right-40 w-[28rem] h-[28rem] rounded-full bg-[#39FF14]/15 blur-[140px]"
        animate={{ scale: [1, 1.25, 1] }}
        transition={{ duration: 9, repeat: Infinity, delay: 1.5 }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
      />

      <Toaster position="top-center" />

      <div className="relative w-full max-w-md mx-auto px-5 pb-12 pt-7">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-7"
        >
          <button
            onClick={() => navigate(`/request/${code}`)}
            className="text-white/50 hover:text-white transition-colors flex items-center gap-2"
            data-testid="games-back"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-['JetBrains_Mono']">Back</span>
          </button>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#FFD700] font-['JetBrains_Mono']">
            <Trophy className="w-3 h-3" /> Win a coupon
          </span>
        </motion.div>

        {/* Hero typography */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8"
        >
          <p className="text-[11px] uppercase tracking-[0.45em] text-white/50 font-['JetBrains_Mono'] mb-3">
            {config.venue_name} · Bar Games
          </p>
          <h1 className="text-[58px] sm:text-7xl leading-[0.85] font-black text-white font-['Outfit'] tracking-[-0.045em]">
            Play.<br />
            <span className="italic font-extralight text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] via-[#FFD700] to-[#39FF14]">
              Win it.
            </span>
          </h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="origin-left h-1.5 w-24 bg-[#FFD700] mt-5 rounded-full"
            style={{ boxShadow: '0 0 24px rgba(255,215,0,0.7)' }}
          />
          <p className="text-white/60 text-base mt-5 font-light font-['IBM_Plex_Sans']">
            Beat a game — unlock a real coupon at the bar.
          </p>
        </motion.div>

        {/* Bento grid */}
        <motion.div
          className="space-y-3"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } } }}
        >
          {games.map((g, idx) => {
            const meta = GAME_META[g];
            const Icon = meta.Icon;
            const reward = config.rewards?.[g];
            const isFeature = idx === 0;
            return (
              <motion.button
                key={g}
                onClick={() => startGame(g)}
                data-testid={`game-pick-${g}`}
                variants={{
                  hidden: { opacity: 0, y: 30, scale: 0.95 },
                  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
                }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full relative overflow-hidden rounded-3xl border border-white/10 text-left group ${isFeature ? 'h-44' : 'h-36'}`}
              >
                {/* Hero photo */}
                <div
                  aria-hidden
                  className="absolute inset-0 bg-cover bg-center scale-110 group-hover:scale-115 transition-transform duration-700"
                  style={{ backgroundImage: `url(${meta.img})` }}
                />
                {/* Color wash */}
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(135deg, ${meta.accent}55 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.92) 100%)` }}
                />
                {/* Edge glow on hover */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ boxShadow: `inset 0 0 60px ${meta.accent}80, 0 0 40px ${meta.accent}40` }}
                />

                <div className="relative h-full p-5 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div
                      className="w-12 h-12 rounded-2xl backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: `${meta.accent}40` }}
                    >
                      <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    {reward && (
                      <span
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-['JetBrains_Mono'] font-bold px-3 py-1.5 rounded-full backdrop-blur-md"
                        style={{ color: meta.accent, backgroundColor: 'rgba(0,0,0,0.55)', border: `1px solid ${meta.accent}50` }}
                      >
                        <Trophy className="w-3 h-3" /> {reward}
                      </span>
                    )}
                  </div>
                  <div className="bg-gradient-to-t from-black/90 via-black/55 to-transparent -mx-5 -mb-5 px-5 pb-5 pt-8 rounded-b-3xl">
                    <p className="text-[10px] uppercase tracking-[0.35em] font-['JetBrains_Mono'] mb-1.5 font-bold" style={{ color: meta.accent }}>
                      {meta.tag}
                    </p>
                    <h3 className={`text-white font-['Outfit'] font-black tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)] leading-[1.05] ${isFeature ? 'text-3xl' : 'text-2xl'}`}>
                      {meta.label}
                    </h3>
                    <p className="text-white/60 text-[11px] mt-1 font-['IBM_Plex_Sans'] font-light">
                      {meta.desc}
                    </p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </motion.div>

        {games.length === 0 && (
          <p className="text-white/40 text-center text-sm font-['IBM_Plex_Sans'] mt-10">
            No games available right now.
          </p>
        )}
      </div>
    </div>
  );
};

export default GuestGamesPage;
